/**
 * ResetPasswordPage — Screen 4 of the auth flow (design: 05-auth.md).
 *
 * Route: /reset-password?token=<token>
 *
 * This screen is reached from the password reset email link. It extracts
 * the token from the query string and submits it with the new password.
 *
 * TC-009: If the token is expired (server returns 410) or invalid (400),
 * shows Screen 4b (expired link) with a "Request a new link" action.
 */

import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthCard } from './AuthCard';
import { PasswordField } from './PasswordField';
import { resetPassword, AuthApiError } from '../../api/auth';

const MIN_PASSWORD_LEN = 8;

type PageState = 'form' | 'expired' | 'success';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [state, setState] = useState<PageState>('form');
  const [submitting, setSubmitting] = useState(false);

  function validatePassword(): boolean {
    if (password.length < MIN_PASSWORD_LEN) {
      setPasswordError(`Password must be at least ${MIN_PASSWORD_LEN} characters.`);
      return false;
    }
    setPasswordError('');
    return true;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!validatePassword()) {
      const pwInput = document.getElementById('reset-password');
      if (pwInput instanceof HTMLInputElement) pwInput.focus();
      return;
    }

    setSubmitting(true);
    try {
      await resetPassword(token, password);
      // TC-009 success path: session is set server-side; redirect to watchlist.
      navigate('/watchlist', { replace: true });
    } catch (err) {
      if (err instanceof AuthApiError && (err.status === 410 || err.status === 400)) {
        // TC-009: expired or already-used token — show Screen 4b.
        setState('expired');
      } else {
        setPasswordError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (state === 'expired') {
    return (
      <AuthCard>
        {/* Screen 4b: Reset Link Expired */}
        <h1 className="text-lg font-bold text-gray-900 mb-3">This link has expired.</h1>
        <p className="text-sm text-gray-600 mb-5">
          Reset links are valid for 1 hour.
        </p>
        <div className="flex flex-col gap-3">
          <Link
            to="/forgot-password"
            className="w-full text-center bg-brand-primary text-white font-semibold py-2.5 min-h-[44px] flex items-center justify-center rounded-lg hover:bg-brand-primary-dk motion-safe:transition-colors"
          >
            Request a new link
          </Link>
          <Link
            to="/login"
            className="text-sm text-brand-primary hover:underline text-center"
          >
            &#8592; Back to log in
          </Link>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      {/* Screen 4: Set a new password */}
      <h1 className="text-lg font-bold text-gray-900 mb-5">Set a new password</h1>

      <form onSubmit={handleSubmit} noValidate>
        <PasswordField
          id="reset-password"
          label="New password"
          value={password}
          onChange={setPassword}
          onBlur={validatePassword}
          error={passwordError}
          helperText="Minimum 8 characters"
          autoComplete="new-password"
        />

        <button
          type="submit"
          disabled={submitting || !token}
          className="w-full bg-brand-primary text-white font-semibold py-2.5 min-h-[44px] rounded-lg hover:bg-brand-primary-dk focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 motion-safe:transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Saving…' : 'Set new password'}
        </button>
      </form>

      {!token && (
        <p className="mt-4 text-sm text-red-600 text-center">
          Invalid reset link. Please{' '}
          <Link to="/forgot-password" className="underline">
            request a new one
          </Link>
          .
        </p>
      )}
    </AuthCard>
  );
}
